/**
 * Generating tiling height map textures based on design material SDF patterns. These textures are intended for parallax map rendering.
 * @module
 */
import degreeToRadian from "common/utils/degreeToRadian";
import { aabb2New } from "geom/aabb2Types";
import * as THREE from "three";
import { decodeIdGLSL } from "../../gpuPicking";
import { setMeshIdAttribute, setMeshIdAttributeToZero } from "../../meshIdAttribute";
import type { ExtendedMesh } from "../../threejs/three-extensions";
import {
  SdfPeriodPurpose,
  getCellZBoundsRelativeToTop,
  getMinFeatureSize,
  hasSdfDistortions,
  normalizeSdfPatternRotation,
  sdfPeriod,
} from "../designMaterialParams";
import type { DesignMaterialParamsPreprocessed, SdfPatternPreprocessed } from "../designMaterialTypes";
import { clearCellGeometryCache, fetchCellGeometryAsMesh } from "../discretization/generateCellGeometry";
import { DISTORTED_SDF_MIN_VERTEX_INTERVAL } from "../discretization/sdfDistortion";
import { sdfCellBoundariesInAABB } from "../discretization/sdfLayout";
import { Bakeable, bakeMaterialVertShader, getBakeMaterialFragShader, getBakeMaterialUniforms } from "./bakeShader";
import { generateHierarchicalDepthMap } from "./generateHierarchicalDepthMap";
import { materialMaskColorAsVec4Uniform } from "./getMaskFromBitmap";

// This is here to help with testing different parallax map resolutions.
export const HEIGHT_MAP_GEN_RESOLUTION_MULTIPLIER = 1;

const DEFAULT_MAX_PIXEL_SIZE = 1024 * HEIGHT_MAP_GEN_RESOLUTION_MULTIPLIER;

const scene = new THREE.Scene();

const bakeChannels = [Bakeable.DEPTH];

const distortionDefinesGLSL = `

vec3 hash32(vec2 p) {
	vec3 p3 = fract(vec3(p.xyx) * vec3(.1031, .1030, .0973));
  p3 += dot(p3, p3.yxz + 33.33);
  return fract((p3.xxy + p3.yzz) * p3.zyx);
}

vec2 hash21(float p) {
	vec3 p3 = fract(vec3(p) * vec3(.1031, .1030, .0973));
	p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.xx + p3.yz) * p3.zy);
}

${decodeIdGLSL}

attribute vec4 id;

uniform vec2 distortionRepeat0;
uniform vec2 distortionPeriod0;
uniform vec3 distortionStrength0;
uniform float distortionRandomness0;

uniform vec2 distortionRepeat1;
uniform vec2 distortionPeriod1;
uniform vec3 distortionStrength1;
uniform float distortionRandomness1;

uniform vec2 distortionRepeat2;
uniform vec2 distortionPeriod2;
uniform vec3 distortionStrength2;
uniform float distortionRandomness2;

vec3 distortionPass(vec2 worldPosXy, vec2 repeat, vec2 period, vec3 strength, float randomness) {
  vec2 gridCoords = worldPosXy / period + hash21(decodeId(id) / 255.0) * randomness;
  vec2 cellIndex = floor(gridCoords);
  vec3 randX0Y0 = hash32(mod(cellIndex, repeat));
  vec3 randX0Y1 = hash32(mod(cellIndex + vec2(0, 1), repeat));
  vec3 randX1Y0 = hash32(mod(cellIndex + vec2(1, 0), repeat));
  vec3 randX1Y1 = hash32(mod(cellIndex + vec2(1, 1), repeat));
  vec2 interpolationT = gridCoords - cellIndex;
  vec3 interpolatedOffset = mix(
    mix(randX0Y0, randX0Y1, interpolationT.y),
    mix(randX1Y0, randX1Y1, interpolationT.y),
  interpolationT.x);
  return (interpolatedOffset + vec3(-0.5, -0.5, -1.0)) * strength;
}
`;

function callDistortionPassGLSL(index: number, worldPosVar: string = "worldPos") {
  return `
    vec3 passOffset${index} = distortionPass(${worldPosVar}.xy, distortionRepeat${index}, distortionPeriod${index}, distortionStrength${index}, distortionRandomness${index});
    distortOffset += vec4(passOffset${index}, 0.0);
  `;
}

function distortionGLSL(worldPosVar: string = "worldPos") {
  return `
  {
    vec4 distortOffset = vec4(0.0);

    ${callDistortionPassGLSL(0, worldPosVar)}
    ${callDistortionPassGLSL(1, worldPosVar)}
    ${callDistortionPassGLSL(2, worldPosVar)}

    if (id.x + id.y + id.z + id.w < 0.01) {
      distortOffset = vec4(0.0); // The back plane doesn't have an id, this keeps it in place.
    }

    ${worldPosVar} = ${worldPosVar} + distortOffset;
  }`;
}

const depthBakingMaterial = new THREE.ShaderMaterial({
  vertexShader: bakeMaterialVertShader
    .replace(
      "// <define_entry_point>",
      `// <define_entry_point>
  ${distortionDefinesGLSL}
`,
    )
    .replace(
      "// <world_pos_entry_point>",
      `// <world_pos_entry_point>
  ${distortionGLSL("worldPos")}
  `,
    ),
  fragmentShader: getBakeMaterialFragShader(bakeChannels),
  uniforms: getBakeMaterialUniforms(bakeChannels),
  name: "heightMapFromDesignMaterial depthBakingMaterial",
});

const cellMaterialVertexShader = `
${distortionDefinesGLSL}
void main() {
  vec4 worldPos = vec4( position, 1.0 );

  #ifdef USE_INSTANCING
  worldPos = instanceMatrix * worldPos;
  #endif
  worldPos = modelMatrix * worldPos;

  ${distortionGLSL("worldPos")}

  gl_Position = projectionMatrix * viewMatrix * worldPos;
}`;

const cellMaterialFragmentShader = `
uniform vec4 color;
void main() {
  gl_FragColor = color;
}`;

const randomMaterials: THREE.ShaderMaterial[] = [];
function getCellRandomMaterial(
  patternIndex: number,
  wrappingCellX: number,
  wrappingCellY: number,
  rand: number,
): THREE.Material {
  const numColors = 128; // Using all unique colors in a 8-bit normalized buffer that are above 0.5.
  const colorIndex = Math.min(Math.floor(rand * numColors), numColors - 1);
  // We assign a unique index to the material according to its parameters. wrappingCellX and wrappingCellY can be either -1, 0, or 1.
  // See sdfLayout.ts for an explanation of the wrappingCell values.
  if (!(wrappingCellX === -2 || wrappingCellX === -1 || wrappingCellX === 0 || wrappingCellX === 1)) {
    console.assert(false);
  }
  if (!(wrappingCellY === -2 || wrappingCellY === -1 || wrappingCellY === 0 || wrappingCellY === 1)) {
    console.assert(false);
  }
  const NUM_UNIQUE_WRAPPING_CELL_VALUES = 4;
  const uniqueMaterialIndex =
    ((colorIndex + patternIndex * numColors) * NUM_UNIQUE_WRAPPING_CELL_VALUES + (wrappingCellX + 2)) *
      NUM_UNIQUE_WRAPPING_CELL_VALUES +
    (wrappingCellY + 1);
  if (randomMaterials[uniqueMaterialIndex] === undefined) {
    const c = (colorIndex + 128) / 255;
    const material = new THREE.ShaderMaterial({
      vertexShader: cellMaterialVertexShader,
      fragmentShader: cellMaterialFragmentShader,
      uniforms: {
        color: {
          value: materialMaskColorAsVec4Uniform(c, patternIndex, wrappingCellX, wrappingCellY),
        },
      },
      name: "heightMapFromDesignMaterial cellRandomMaterial",
    });
    randomMaterials[uniqueMaterialIndex] = material;
  }
  return randomMaterials[uniqueMaterialIndex];
}

const sharedDepthBuffers = {};
function getSharedDepthBuffer(pixelSize: number) {
  if (!sharedDepthBuffers.hasOwnProperty(pixelSize)) {
    const depthBuffer = new THREE.DepthTexture(pixelSize, pixelSize);
    sharedDepthBuffers[pixelSize] = depthBuffer;
  }
  return sharedDepthBuffers[pixelSize];
}

const backgroundGeometry = new THREE.PlaneGeometry(1, 1, 1, 1);
backgroundGeometry.userData.isCached = true;

function copyTextureFromRenderTarget(
  renderer: THREE.WebGLRenderer,
  renderTarget: THREE.WebGLRenderTarget,
): THREE.Texture {
  const srcTex = renderTarget.texture;
  if (
    srcTex.mapping === THREE.CubeReflectionMapping ||
    srcTex.mapping === THREE.CubeRefractionMapping ||
    srcTex.mapping === THREE.CubeUVReflectionMapping
  ) {
    throw "cube textures not supported";
  }
  const copy = new THREE.DataTexture(
    null,
    renderTarget.width,
    renderTarget.height,
    srcTex.format as THREE.PixelFormat, // cast is required because some textures might have a compressed or depth format, those are not supported here.
    srcTex.type,
    srcTex.mapping,
    srcTex.wrapS,
    srcTex.wrapT,
    srcTex.magFilter,
    srcTex.minFilter,
    srcTex.anisotropy,
  );
  renderer.setRenderTarget(renderTarget);
  renderer.copyFramebufferToTexture(copy, new THREE.Vector2(0, 0), 0);
  return copy;
}

// Map from WebGLRenderers to height map caches.
const heightMapCaches: WeakMap<THREE.WebGLRenderer, { [key: string]: HeightMap }> = new WeakMap();

export function heightMapCacheCount(renderer: THREE.WebGLRenderer): number {
  const cache = heightMapCaches.get(renderer);
  if (cache === undefined) {
    return 0;
  }
  return Object.keys(cache).length;
}

export function estimateHeightMapGPUMemory(renderer: THREE.WebGLRenderer): number {
  const cache = heightMapCaches.get(renderer);
  if (cache === undefined) {
    return 0;
  }
  let total = 0;
  for (const cachedValue of Object.values(cache)) {
    let numChannels = 0;
    if (cachedValue.maskTexture) {
      numChannels += 4;
    }
    if (cachedValue.texture) {
      if (cachedValue.texture.format === THREE.RedFormat) {
        numChannels += 1;
      } else if (cachedValue.texture.format === THREE.RGFormat) {
        numChannels += 2;
      } else {
        numChannels += 4;
      }
    }
    total += cachedValue.pixelSize * cachedValue.pixelSize * numChannels;
  }
  return total;
}

// Clear all cached height maps for a specific renderer.
export async function forgetCachedHeightmaps(renderer: THREE.WebGLRenderer, keepHeightMaps?: HeightMap[]) {
  if (heightMapCaches.get(renderer) === undefined) {
    return;
  }
  const cache = heightMapCaches.get(renderer);
  if (!cache) {
    return;
  }
  const forgetKeys = Object.keys(cache);
  for (const key of forgetKeys) {
    const map = await cache[key];
    if (keepHeightMaps === undefined || keepHeightMaps.indexOf(map) < 0) {
      map.texture.dispose();
      if (map.maskTexture) {
        map.maskTexture.dispose();
      }
      if (map.renderTarget) {
        map.renderTarget.dispose();
      }
      if (map.maskRenderTarget) {
        map.maskRenderTarget.dispose();
      }
      delete cache[key];
    }
  }
}

const cacheIgnoreSdfKeys = ["aoStrength", "aoTolerance", "useAO", "useShadows"];

function getCachedHeightMap(
  renderer: THREE.WebGLRenderer,
  params: DesignMaterialParamsPreprocessed,
  renderMask: boolean,
  renderHierarchicalDepth: boolean,
  maxPixelSize?: number,
): HeightMap | null {
  if (heightMapCaches.get(renderer) === undefined) {
    heightMapCaches.set(renderer, {});
  }
  const cacheKeyData: any[] = [];
  for (const key of Object.keys(params.sdf.common)) {
    if (cacheIgnoreSdfKeys.indexOf(key) === -1) {
      cacheKeyData.push(params.sdf.common[key]);
    }
  }
  if (params.sdf.common.randomTilesMode === "RandomSplit") {
    cacheKeyData.push("RANDOM");
    for (const key of Object.keys(params.sdf.randomTiles)) {
      if (cacheIgnoreSdfKeys.indexOf(key) === -1) {
        cacheKeyData.push(params.sdf.randomTiles[key]);
      }
    }
  }
  for (let i = 0; i < params.sdf.patterns.length; ++i) {
    cacheKeyData.push("p" + i);
    for (const key of Object.keys(params.sdf.patterns[i])) {
      cacheKeyData.push(params.sdf.patterns[i][key]);
    }
  }
  for (let i = 0; i < params.sdf.distortions.length; ++i) {
    if (params.sdf.distortions[i].enabled) {
      cacheKeyData.push("d" + i);
      for (const key of Object.keys(params.sdf.distortions[i])) {
        cacheKeyData.push(params.sdf.distortions[i][key]);
      }
    }
  }
  cacheKeyData.push(renderMask);
  cacheKeyData.push(renderHierarchicalDepth);
  cacheKeyData.push(maxPixelSize);
  const cacheKey = cacheKeyData.join(".");
  const cache = heightMapCaches.get(renderer);
  if (!cache) {
    return null;
  }
  if (cache.hasOwnProperty(cacheKey)) {
    return cache[cacheKey];
  }
  const heightMap = heightMapFromDesignMaterialInternal(
    renderer,
    params,
    renderMask,
    renderHierarchicalDepth,
    maxPixelSize,
  );
  cache[cacheKey] = heightMap;
  return heightMap;
}

export interface HeightMap {
  texture: THREE.Texture;
  maskTexture: THREE.Texture | null;
  pixelSize: number;
  worldXSize: number;
  worldYSize: number;
  worldZDepth: number;
  rotation: number; // Rotation of the heightmap in UV space.

  // The render targets are only here for memory management - caller needs to free them once it's done with the texture.
  renderTarget?: THREE.WebGLRenderTarget;
  maskRenderTarget?: THREE.WebGLRenderTarget;
}

export function renderHeightMapRenderer(
  params: DesignMaterialParamsPreprocessed,
  maxPixelSize: number = DEFAULT_MAX_PIXEL_SIZE,
) {
  const parent = new THREE.Object3D();
  const geometries: THREE.BufferGeometry[] = [];

  const { normalizedPatterns, rotation } = normalizeSdfPatternRotation(params.sdf.common, params.sdf.patterns);
  let { repeatPeriodX: worldXSize, repeatPeriodY: worldYSize } = sdfPeriod(
    params.sdf.common,
    normalizedPatterns,
    SdfPeriodPurpose.HeightMapPeriod,
  );
  if (!((worldXSize === undefined) === (worldYSize === undefined))) {
    console.assert(false);
  }
  if (worldXSize === undefined || worldYSize === undefined) {
    worldYSize = 100;
    worldXSize = 100;
  }

  for (const pattern of normalizedPatterns) {
    const continuousSize = Math.max(worldXSize, worldYSize) * 3;
    if (pattern.continuousX) {
      pattern.xSize = continuousSize;
      pattern.xOffset = continuousSize * 0.33;
      pattern.xDistance = pattern.xSize;
    } else if (pattern.continuousY) {
      pattern.ySize = continuousSize;
      pattern.yOffset = continuousSize * 0.33;
      pattern.yDistance = pattern.ySize;
    }
  }

  const sdfDistorted = hasSdfDistortions(params.sdf.distortions);
  const minFeatureSize = getMinFeatureSize(params.sdf);

  const pixelSizeFromWorldSize = Math.min(
    maxPixelSize * 0.5,
    Math.max(worldXSize, worldYSize) * 16.0 * HEIGHT_MAP_GEN_RESOLUTION_MULTIPLIER,
  );
  const pixelSizeFromMinFeatureSize =
    Math.max(worldXSize / minFeatureSize, worldYSize / minFeatureSize) * HEIGHT_MAP_GEN_RESOLUTION_MULTIPLIER;
  const pixelSize = Math.min(
    maxPixelSize,
    THREE.MathUtils.ceilPowerOfTwo(Math.max(pixelSizeFromWorldSize, pixelSizeFromMinFeatureSize)),
  );

  const sdfPatternsWithGeo: { patternParams: SdfPatternPreprocessed; geometry: THREE.BufferGeometry }[] = [];

  let maxCellZ = 0;

  for (const patternParams of normalizedPatterns) {
    const roundSidesAllowsGapAlignment =
      patternParams.roundStrength === 0 ||
      patternParams.roundSidesMode === "none" ||
      (patternParams.roundSidesMode === "roundZ" &&
        patternParams.roundStrength < Math.max(patternParams.xSize, patternParams.ySize) * 0.25);
    if (patternParams.rotation === 0 && patternParams.slope === 0 && roundSidesAllowsGapAlignment) {
      // Round small gaps between tiles so that they cover a specific number of pixels.
      // This way the width of the gaps won't vary in the height map.
      const xToPixels = (x) => (x / worldXSize) * pixelSize;
      const xToWorld = (x) => (x * worldXSize) / pixelSize;
      const yToPixels = (y) => (y / worldYSize) * pixelSize;
      const yToWorld = (y) => (y * worldYSize) / pixelSize;
      const xGap = patternParams.xDistance - patternParams.xSize;
      const yGap = patternParams.yDistance - patternParams.ySize;
      const xGapInPixels = xToPixels(xGap);
      const yGapInPixels = yToPixels(yGap);
      if (xGap > 0.01 && xGapInPixels < 2) {
        patternParams.xSize = xToWorld(xToPixels(patternParams.xDistance) - Math.ceil(xGapInPixels));
      }
      if (yGap > 0.01 && yGapInPixels < 2) {
        patternParams.ySize = yToWorld(yToPixels(patternParams.yDistance) - Math.ceil(yGapInPixels));
      }
    }
    const cellMesh = fetchCellGeometryAsMesh(
      patternParams,
      params.sdf.common,
      sdfDistorted,
      sdfDistorted ? DISTORTED_SDF_MIN_VERTEX_INTERVAL : Infinity,
      false,
    );

    maxCellZ = Math.max(maxCellZ, patternParams.zSize);

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.BufferAttribute(cellMesh.verts, 3));
    geometry.index = new THREE.BufferAttribute(cellMesh.tris, 1);

    sdfPatternsWithGeo.push({
      patternParams,
      geometry,
    });
    geometries.push(geometry);
  }

  clearCellGeometryCache();

  let maxVaryHeightScale = 0;
  for (const pattern of sdfPatternsWithGeo) {
    maxVaryHeightScale = Math.max(
      maxVaryHeightScale,
      pattern.patternParams.varyHeight ? pattern.patternParams.varyHeightScale : 0.0,
    );
  }

  // The cell geometry z values are relative to the SDF base plane, and that's where we place the far plane.

  let sdfDepthOffset = 0;
  if (params.transparency === "sdfTransparent" || params.transparency === "metalScreen") {
    // In case of transparent SDF, the sdf.depth parameter is ignored and we fit the height range to the SDF cells.
    sdfDepthOffset = getCellZBoundsRelativeToTop(params.sdf.common, normalizedPatterns)[1] - params.sdf.common.depth;
  }

  const zPadding = 0.001;
  const camFar = 100;
  const worldZDepth = maxCellZ + sdfDepthOffset + maxVaryHeightScale * 0.5 + zPadding * 2;
  const camNear = camFar - worldZDepth;
  const camera = new THREE.OrthographicCamera(0, worldXSize, worldYSize, 0, camNear, camFar);

  parent.add(camera);
  camera.position.set(0, 0, camFar - zPadding);
  camera.lookAt(0, 0, 0);
  camera.updateMatrixWorld();

  const bgSize = Math.max(worldXSize, worldYSize) + 1;
  const backgroundMesh = new THREE.Mesh(
    backgroundGeometry,
    new THREE.MeshBasicMaterial({ color: 0x000000, name: "heightMapFromDesignMaterial backgroundMesh" }),
  );
  parent.add(backgroundMesh);
  setMeshIdAttributeToZero(backgroundMesh);
  backgroundMesh.scale.set(bgSize, bgSize, 1);
  backgroundMesh.position.set(worldXSize * 0.5, worldYSize * 0.5, 0);
  backgroundMesh.updateMatrixWorld();

  let patternIndex = 0;

  const uvAABB = aabb2New(0, 0, worldXSize, worldYSize);
  for (const pattern of sdfPatternsWithGeo) {
    const cellLocations: Generator<number[]> = sdfCellBoundariesInAABB(
      params.sdf.common,
      params.sdf.randomTiles,
      pattern.patternParams,
      patternIndex,
      uvAABB,
      true,
      worldXSize,
      worldYSize,
    );
    let cellLocation = cellLocations.next();
    const slopeRotation = params.sdf.common.slopeAxis === "Z" ? pattern.patternParams.slope : 0;
    const cellRotation = pattern.patternParams.rotation + slopeRotation;
    while (!cellLocation.done) {
      const [centerX, centerY, xSize, ySize, rand, randMask, wrappingCellX, wrappingCellY] =
        cellLocation.value as number[];
      const mesh = new THREE.Mesh(
        pattern.geometry,
        getCellRandomMaterial(patternIndex, wrappingCellX, wrappingCellY, randMask),
      );
      setMeshIdAttribute(mesh, Math.floor(randMask * 255));
      parent.add(mesh);
      const z =
        (pattern.patternParams.varyHeight ? pattern.patternParams.varyHeightScale : 0.0) * (rand - 0.5) +
        sdfDepthOffset;
      mesh.scale.set(xSize / pattern.patternParams.xSize, ySize / pattern.patternParams.ySize, 1.0);
      mesh.rotation.set(0, 0, degreeToRadian(cellRotation));
      mesh.position.set(centerX, centerY, z);
      mesh.updateMatrixWorld();
      cellLocation = cellLocations.next();
    }

    ++patternIndex;
  }

  return { parent, camera, worldXSize, worldYSize, pixelSize, worldZDepth, rotation, geometries };
}

export function heightMapFromDesignMaterial(
  renderer: THREE.WebGLRenderer,
  params: DesignMaterialParamsPreprocessed,
  renderMask: boolean,
  renderHierarchicalDepth: boolean,
  maxPixelSize?: number,
): HeightMap | null {
  if (params.sdf.patterns.length === 0) {
    return null;
  }
  return getCachedHeightMap(renderer, params, renderMask, renderHierarchicalDepth, maxPixelSize);
}

function heightMapFromDesignMaterialInternal(
  renderer: THREE.WebGLRenderer,
  params: DesignMaterialParamsPreprocessed,
  renderMask: boolean,
  renderHierarchicalDepth: boolean,
  maxPixelSize: number = DEFAULT_MAX_PIXEL_SIZE,
): HeightMap {
  const { parent, camera, worldXSize, worldYSize, pixelSize, worldZDepth, rotation, geometries } =
    renderHeightMapRenderer(params, maxPixelSize);

  scene.add(parent);

  const distortionUniforms: any = {};

  for (let i = 0; i < params.sdf.distortions.length; ++i) {
    const distortion = params.sdf.distortions[i];
    const desiredDistortionPeriod = [distortion.periodX, distortion.periodY];
    const cellCount = [
      Math.ceil(worldXSize / desiredDistortionPeriod[0]),
      Math.ceil(worldYSize / desiredDistortionPeriod[1]),
    ];

    distortionUniforms["distortionPeriod" + i] = { value: [worldXSize / cellCount[0], worldYSize / cellCount[1]] };
    distortionUniforms["distortionRepeat" + i] = { value: cellCount };
    distortionUniforms["distortionStrength" + i] = {
      value: [
        distortion.enabled ? distortion.strengthX : 0,
        distortion.enabled ? distortion.strengthY : 0,
        distortion.enabled ? distortion.strengthZ : 0,
      ],
    };
    distortionUniforms["distortionRandomness" + i] = { value: distortion.randomness };
  }

  for (const uniformName of Object.keys(distortionUniforms)) {
    depthBakingMaterial.uniforms[uniformName] = distortionUniforms[uniformName];
  }

  const depthTexturesSupported = renderer.capabilities.isWebGL2 || renderer.extensions.get("WEBGL_depth_texture");

  // TODO: We could consider reusing the whole render target in case we'll switch over to using hierarchical depth maps.
  let renderTarget: THREE.WebGLRenderTarget | undefined = new THREE.WebGLRenderTarget(pixelSize, pixelSize, {
    minFilter: THREE.LinearFilter,
    magFilter: THREE.LinearFilter,
    wrapS: THREE.RepeatWrapping,
    wrapT: THREE.RepeatWrapping,
    format: THREE.RedFormat,
    depthBuffer: true,
    stencilBuffer: false,
  });

  if (depthTexturesSupported) {
    renderTarget.depthTexture = getSharedDepthBuffer(pixelSize);
  }

  scene.overrideMaterial = depthBakingMaterial;
  renderer.setRenderTarget(renderTarget);
  renderer.clearDepth();
  renderer.render(scene, camera);
  scene.overrideMaterial = null;

  let texture = renderTarget.texture;
  if (renderHierarchicalDepth) {
    texture = generateHierarchicalDepthMap(renderer, renderTarget);
  }
  if (depthTexturesSupported) {
    renderTarget.depthTexture = null;
    if (renderHierarchicalDepth) {
      renderTarget.dispose();
      renderTarget = undefined;
    }
  } else {
    // We need to free the depth buffer. Since three.js only supports freeing the whole render target at once, we need to first copy out the texture.
    if (!renderHierarchicalDepth) {
      texture = copyTextureFromRenderTarget(renderer, renderTarget);
    }
    renderTarget.dispose();
    renderTarget = undefined;
  }

  let maskTexture: THREE.Texture | null = null;
  let maskRenderTarget: THREE.WebGLRenderTarget | undefined = undefined;
  if (renderMask) {
    maskRenderTarget = new THREE.WebGLRenderTarget(pixelSize, pixelSize, {
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
      wrapS: THREE.RepeatWrapping,
      wrapT: THREE.RepeatWrapping,
      format: THREE.RGBAFormat,
      depthBuffer: true,
      stencilBuffer: false,
    });

    if (depthTexturesSupported) {
      maskRenderTarget.depthTexture = getSharedDepthBuffer(pixelSize);
    }

    for (const randomMaterial of randomMaterials) {
      if (randomMaterial !== undefined) {
        for (const uniformName of Object.keys(distortionUniforms)) {
          randomMaterial.uniforms[uniformName] = distortionUniforms[uniformName];
        }
      }
    }

    renderer.setRenderTarget(maskRenderTarget);
    renderer.clearDepth();
    renderer.render(scene, camera);
    maskTexture = maskRenderTarget.texture;
    maskTexture.anisotropy = 0;

    if (depthTexturesSupported) {
      maskRenderTarget.depthTexture = null;
    } else {
      // We need to free the depth buffer. Since three.js only supports freeing the whole render target at once, we need to first copy out the texture.
      maskTexture = copyTextureFromRenderTarget(renderer, maskRenderTarget);
      maskRenderTarget.dispose();
      maskRenderTarget = undefined;
    }
  }

  scene.remove(parent);

  renderer.setRenderTarget(null);

  parent.traverse((obj) => {
    if (obj instanceof THREE.Mesh) {
      (obj as ExtendedMesh).dispose();
    }
  });

  for (const geom of geometries) {
    geom.dispose();
  }

  return {
    texture,
    maskTexture,
    pixelSize,
    worldXSize,
    worldYSize,
    worldZDepth,
    rotation: rotation ?? 0,
    renderTarget,
    maskRenderTarget,
  };
}
